import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SEO from '../../components/SEO';
import { useTranslation } from 'react-i18next';
import {
    User,
    Mail,
    Lock,
    Bell,
    Shield,
    CreditCard,
    Moon,
    Sun,
    Check,
    Loader2,
    LogOut,
    Eye,
    EyeOff,
    Settings as SettingsIcon,
    AlertCircle,
    Trash2,
    X,
    Crown,
    Calendar,
    ExternalLink
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';

import { translateAuthError } from '../../utils/authErrors';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Settings() {
    const { t, i18n } = useTranslation();
    const { theme, toggleTheme } = useTheme();
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Profile state
    const [profile, setProfile] = useState({
        full_name: '',
        email: '',
    });

    // Password state
    const [passwords, setPasswords] = useState({
        current: '',
        new: '',
        confirm: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });

    // Delete account state
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Subscription state
    const [subscription, setSubscription] = useState<any>(null);
    const [portalLoading, setPortalLoading] = useState(false);

    useEffect(() => {
        checkUser();
    }, []);

    const checkUser = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            setUser(user);
            setProfile({
                full_name: user.user_metadata?.full_name || '',
                email: user.email || '',
            });
            await fetchSubscription(user.id);
        }
        setLoading(false);
    };

    const fetchSubscription = async (userId: string) => {
        try {
            const { data } = await supabase
                .from('subscriptions')
                .select('*')
                .eq('user_id', userId)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

            setSubscription(data);
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    };

    const handleOpenPortal = async () => {
        try {
            setPortalLoading(true);
            const { data: { session } } = await supabase.auth.getSession();

            const { data, error } = await supabase.functions.invoke('create-portal-session', {
                headers: {
                    Authorization: `Bearer ${session?.access_token}`,
                    'Accept-Language': i18n.language,
                }
            });

            if (error) throw error;
            if (data?.url) {
                window.location.href = data.url;
            } else if (data?.error) {
                throw new Error(data.error);
            }
        } catch (error: any) {
            console.error('Error opening portal:', error);
            alert(`${t('settings.billing.portal_error')} : ${error.message || t('settings.billing.portal_error_help')}`);
        } finally {
            setPortalLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setUpdating(true);
        setMessage({ type: '', text: '' });

        try {
            const { error } = await supabase.auth.updateUser({
                data: { full_name: profile.full_name }
            });

            if (error) throw error;
            setMessage({ type: 'success', text: t('settings.profile.success') });
        } catch (error: any) {
            setMessage({ type: 'error', text: translateAuthError(error.message) });
        } finally {
            setUpdating(false);
        }
    };

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (passwords.new !== passwords.confirm) {
            setMessage({ type: 'error', text: t('settings.security.error_match') });
            return;
        }

        if (passwords.new.length < 6) {
            setMessage({ type: 'error', text: t('settings.security.error_length') });
            return;
        }

        setUpdating(true);
        setMessage({ type: '', text: '' });

        try {
            // Re-authenticate to verify current password
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: user.email,
                password: passwords.current
            });

            if (signInError) {
                throw new Error(t('settings.security.error_current'));
            }

            const { error } = await supabase.auth.updateUser({
                password: passwords.new
            });

            if (error) throw error;

            setMessage({ type: 'success', text: t('settings.security.success') });

            // Sign out and redirect after a short delay
            setTimeout(async () => {
                await supabase.auth.signOut();
                window.REACT_APP_NAVIGATE('/login?reset=success');
            }, 1500);
        } catch (error: any) {
            setMessage({ type: 'error', text: translateAuthError(error.message) });
            setUpdating(false);
        }
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        window.REACT_APP_NAVIGATE('/');
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmEmail !== user.email) {
            setMessage({ type: 'error', text: t('settings.delete.error_email') });
            return;
        }

        setIsDeleting(true);
        setMessage({ type: '', text: '' });

        try {
            const { error: rpcError } = await supabase.rpc('delete_user');
            if (rpcError) throw rpcError;

            await supabase.auth.signOut();
            window.REACT_APP_NAVIGATE('/');
        } catch (error: any) {
            setMessage({ type: 'error', text: t('settings.delete.error_generic') });
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-dark-bg flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <DashboardLayout user={user}>
            <SEO noindex={true} />
            <div className="max-w-4xl mx-auto pb-12">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-text-main mb-2 flex items-center gap-2">
                        <SettingsIcon className="text-primary" size={28} />
                        {t('settings.title')}
                    </h1>
                    <p className="text-text-muted">{t('settings.description')}</p>
                </div>

                {message.text && (
                    <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 animate-in fade-in slide-in-from-left-4 ${message.type === 'success'
                        ? 'bg-green-500/10 border-green-500/20 text-green-500'
                        : 'bg-red-500/10 border-red-500/20 text-red-500'
                        }`}>
                        {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
                        <p className="font-medium text-sm">{message.text}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Navigation Sidebar (Local) */}
                    <div className="md:col-span-1 space-y-2">
                        <a href="#profile" className="flex items-center gap-3 p-3 rounded-xl bg-primary/10 text-primary font-bold transition-all border border-primary/20">
                            <User size={20} />
                            {t('settings.nav.profile')}
                        </a>
                        <a href="#appearance" className="flex items-center gap-3 p-3 rounded-xl text-text-muted hover:bg-dark-card hover:text-text-main transition-all font-medium border border-transparent hover:border-dark-border">
                            <Moon size={20} />
                            {t('settings.nav.appearance')}
                        </a>
                        <a href="#notifications" className="flex items-center gap-3 p-3 rounded-xl text-text-muted hover:bg-dark-card hover:text-text-main transition-all font-medium border border-transparent hover:border-dark-border">
                            <Bell size={20} />
                            {t('settings.nav.notifications')}
                        </a>
                        <a href="#security" className="flex items-center gap-3 p-3 rounded-xl text-text-muted hover:bg-dark-card hover:text-text-main transition-all font-medium border border-transparent hover:border-dark-border">
                            <Shield size={20} />
                            {t('settings.nav.security')}
                        </a>
                        <a href="#billing" className="flex items-center gap-3 p-3 rounded-xl text-text-muted hover:bg-dark-card hover:text-text-main transition-all font-medium border border-transparent hover:border-dark-border">
                            <CreditCard size={20} />
                            {t('settings.nav.billing')}
                        </a>
                        <div className="pt-4 border-t border-dark-border">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 p-3 rounded-xl text-red-500 hover:bg-red-500/10 transition-all font-medium"
                            >
                                <LogOut size={20} />
                                {t('settings.nav.logout')}
                            </button>
                        </div>
                    </div>

                    {/* Main Settings Content */}
                    <div className="md:col-span-2 space-y-8">
                        {/* Profile Section */}
                        <section id="profile" className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
                            <div className="p-6 border-b border-dark-border">
                                <h3 className="text-lg font-bold text-text-main">{t('settings.profile.title')}</h3>
                                <p className="text-sm text-text-muted">{t('settings.profile.subtitle')}</p>
                            </div>
                            <form onSubmit={handleUpdateProfile} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-text-main mb-2">{t('settings.profile.name')}</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <User className="text-text-muted" size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            value={profile.full_name}
                                            onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                                            className="w-full pl-11 pr-4 py-2.5 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-all text-sm text-text-main"
                                            placeholder={t('settings.profile.name_placeholder')}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-text-main mb-2">{t('settings.profile.email')}</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <Mail className="text-text-muted" size={18} />
                                        </div>
                                        <input
                                            type="email"
                                            value={profile.email}
                                            disabled
                                            className="w-full pl-11 pr-4 py-2.5 bg-dark-bg/50 border border-dark-border rounded-xl opacity-60 cursor-not-allowed text-sm text-text-main"
                                        />
                                    </div>
                                    <p className="mt-1.5 text-xs text-text-muted flex items-center gap-1.5">
                                        <Shield size={12} />
                                        {t('settings.profile.email_help')}
                                    </p>
                                </div>
                                <div className="pt-2 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={updating}
                                        className="px-6 py-2 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {updating ? <Loader2 className="animate-spin" size={18} /> : t('settings.profile.save')}
                                    </button>
                                </div>
                            </form>
                        </section>

                        {/* Appearance Section */}
                        <section id="appearance" className="bg-dark-card rounded-2xl border border-dark-border p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold text-text-main">{t('settings.appearance.title')}</h3>
                                    <p className="text-sm text-text-muted">{t('settings.appearance.subtitle')}</p>
                                </div>
                                <button
                                    onClick={toggleTheme}
                                    className="px-4 py-2 bg-dark-bg border border-dark-border rounded-xl flex items-center gap-2 text-text-main hover:border-primary/50 transition-all font-medium"
                                >
                                    {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                                    {t('settings.appearance.mode')} {theme === 'dark' ? t('settings.appearance.light') : t('settings.appearance.dark')}
                                </button>
                            </div>
                        </section>

                        {/* Security Section */}
                        <section id="security" className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
                            <div className="p-6 border-b border-dark-border">
                                <h3 className="text-lg font-bold text-text-main">{t('settings.security.title')}</h3>
                                <p className="text-sm text-text-muted">{t('settings.security.subtitle')}</p>
                            </div>
                            <form onSubmit={handleUpdatePassword} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-semibold text-text-main mb-2">{t('settings.security.current_password')}</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <Lock className="text-text-muted" size={18} />
                                        </div>
                                        <input
                                            type={showPasswords.current ? 'text' : 'password'}
                                            value={passwords.current}
                                            onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                                            className="w-full pl-11 pr-11 py-2.5 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-all text-sm text-text-main"
                                            placeholder="••••••••"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-muted hover:text-text-main transition-colors"
                                        >
                                            {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-text-main mb-2">{t('settings.security.new_password')}</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <Lock className="text-text-muted" size={18} />
                                        </div>
                                        <input
                                            type={showPasswords.new ? 'text' : 'password'}
                                            value={passwords.new}
                                            onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                                            className="w-full pl-11 pr-11 py-2.5 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-all text-sm text-text-main"
                                            placeholder="••••••••"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-muted hover:text-text-main transition-colors"
                                        >
                                            {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-text-main mb-2">{t('settings.security.confirm_password')}</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <Lock className="text-text-muted" size={18} />
                                        </div>
                                        <input
                                            type={showPasswords.confirm ? 'text' : 'password'}
                                            value={passwords.confirm}
                                            onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                                            className="w-full pl-11 pr-11 py-2.5 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-all text-sm text-text-main"
                                            placeholder="••••••••"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-text-muted hover:text-text-main transition-colors"
                                        >
                                            {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>
                                <div className="pt-2 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={updating || !passwords.new}
                                        className="px-6 py-2 bg-dark-bg border border-dark-border text-text-main font-bold rounded-xl hover:bg-dark-hover transition-all flex items-center gap-2 disabled:opacity-50"
                                    >
                                        {t('settings.security.update')}
                                    </button>
                                </div>
                            </form>
                        </section>

                        {/* Billing Section */}
                        <section id="billing" className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden">
                            <div className="p-6 border-b border-dark-border flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold text-text-main">{t('settings.billing.title')}</h3>
                                    <p className="text-sm text-text-muted">{t('settings.billing.subtitle')}</p>
                                </div>
                                <div className="p-2 bg-primary/10 rounded-lg border border-primary/20">
                                    <CreditCard className="text-primary" size={24} />
                                </div>
                            </div>
                            <div className="p-6 space-y-6">
                                {/* Plan Details */}
                                <div className="p-4 bg-dark-bg border border-dark-border rounded-xl">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center border border-primary/20">
                                                <Crown className="text-primary" size={20} />
                                            </div>
                                            <div>
                                                <p className="text-xs text-text-muted uppercase font-bold tracking-wider">{t('settings.billing.current_plan')}</p>
                                                <h4 className="text-lg font-bold text-text-main">
                                                    {subscription ? subscription.plan_name : t('settings.billing.free_plan')}
                                                </h4>
                                            </div>
                                        </div>
                                        {subscription && (
                                            <span className={`px-3 py-1 text-xs font-bold rounded-full border ${subscription.status === 'active'
                                                ? 'bg-green-500/10 border-green-500/20 text-green-500'
                                                : 'bg-orange-500/10 border-orange-500/20 text-orange-500'
                                                }`}>
                                                {subscription.status === 'active' ? t('settings.billing.status_active') : t('settings.billing.status_canceled')}
                                            </span>
                                        )}
                                    </div>

                                    {!subscription ? (
                                        <Link
                                            to="/pricing"
                                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all text-sm shadow-lg shadow-primary/20"
                                        >
                                            {t('settings.billing.view_offers')}
                                        </Link>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between text-sm pt-4 border-t border-dark-border/50">
                                                <div className="flex items-center gap-2 text-text-muted">
                                                    <Calendar size={14} />
                                                    <span>
                                                        {subscription.status === 'active' ? t('settings.billing.renewal') : t('settings.billing.ends')} {''}
                                                        {subscription.current_period_end
                                                            ? formatDate(subscription.current_period_end)
                                                            : subscription.end_date
                                                                ? formatDate(subscription.end_date)
                                                                : 'N/A'}
                                                    </span>
                                                </div>
                                                <span className="font-bold text-text-main">
                                                    {subscription.plan_name === 'Premium'
                                                        ? (subscription.plan_type === 'yearly' ? t('settings.billing.price_yearly', { price: '74.95' }) : t('settings.billing.price_monthly', { price: '7.49' }))
                                                        : (subscription.plan_type === 'yearly' ? t('settings.billing.price_yearly', { price: '44.95' }) : t('settings.billing.price_monthly', { price: '4.49' }))}
                                                </span>
                                            </div>
                                            <Link
                                                to="/pricing"
                                                className="w-full flex items-center justify-center gap-2 py-2.5 bg-dark-bg border border-primary/30 text-primary font-bold rounded-xl hover:bg-primary/5 transition-all text-sm"
                                            >
                                                {t('settings.billing.change_plan')}
                                            </Link>
                                        </div>
                                    )}
                                </div>

                                {/* Manage Portal Link */}
                                {subscription && (
                                    <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex items-start gap-4">
                                        <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20 flex-shrink-0">
                                            <ExternalLink className="text-blue-500" size={20} />
                                        </div>
                                        <div>
                                            <h5 className="font-bold text-text-main mb-1">{t('settings.billing.portal_title')}</h5>
                                            <p className="text-xs text-text-muted mb-3">
                                                {t('settings.billing.portal_description')}
                                            </p>
                                            <button
                                                onClick={handleOpenPortal}
                                                disabled={portalLoading}
                                                className="text-sm text-blue-500 hover:text-blue-400 font-bold flex items-center gap-2 transition-colors disabled:opacity-50"
                                            >
                                                {portalLoading ? <Loader2 className="animate-spin" size={14} /> : <ExternalLink size={14} />}
                                                {t('settings.billing.portal_cta')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Dangerous Zone */}
                        <section className="bg-red-500/5 rounded-2xl border border-red-500/10 p-6">
                            <div className="flex items-center gap-4 mb-4 text-red-500">
                                <AlertCircle size={24} />
                                <h3 className="text-lg font-bold">{t('settings.danger.title')}</h3>
                            </div>
                            <p className="text-sm text-text-muted mb-6">
                                {t('settings.danger.description')}
                            </p>
                            <button
                                onClick={() => setIsDeleteModalOpen(true)}
                                className="px-6 py-2 bg-red-500/10 text-red-500 font-bold rounded-xl border border-red-500/20 hover:bg-red-500 hover:text-white transition-all">
                                {t('settings.danger.cta')}
                            </button>
                        </section>
                    </div>
                </div>
            </div>

            {/* Delete Account Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-dark-card border border-dark-border w-full max-w-md rounded-2xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-dark-border flex items-center justify-between">
                            <div className="flex items-center gap-3 text-red-500">
                                <Trash2 size={24} />
                                <h3 className="text-xl font-bold">{t('settings.delete.title')}</h3>
                            </div>
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="text-text-muted hover:text-text-main transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-text-main">
                                {t('settings.delete.warning')}
                            </p>
                            <div className="p-4 bg-red-500/5 border border-red-500/10 rounded-xl">
                                <p className="text-sm text-text-muted mb-3">
                                    {t('settings.delete.confirm_help', { email: user.email })}
                                </p>
                                <input
                                    type="email"
                                    value={deleteConfirmEmail}
                                    onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-dark-bg border border-dark-border rounded-xl focus:border-red-500/50 focus:outline-none transition-all text-sm text-text-main"
                                    placeholder={t('settings.delete.placeholder')}
                                />
                            </div>
                        </div>
                        <div className="p-6 bg-dark-bg/50 border-t border-dark-border flex gap-3">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="flex-1 py-3 bg-dark-card border border-dark-border text-text-main font-bold rounded-xl hover:bg-dark-hover transition-all"
                            >
                                {t('settings.delete.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                disabled={isDeleting || deleteConfirmEmail !== user.email}
                                className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="animate-spin" size={20} />
                                        {t('settings.delete.deleting')}
                                    </>
                                ) : (
                                    t('settings.delete.confirm')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
